import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { translations } from '../data/translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

// Available languages
export const AVAILABLE_LANGUAGES = ['en', 'fr', 'es', 'it', 'de', 'pt'];

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Detect language from URL path
  const getLanguageFromPath = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const firstPart = pathParts[0];
    
    if (AVAILABLE_LANGUAGES.includes(firstPart)) {
      return firstPart;
    }
    return 'en'; // default to English
  };

  const [language, setLanguageState] = useState(getLanguageFromPath());

  // Update language when URL changes
  useEffect(() => {
    const detectedLang = getLanguageFromPath();
    if (detectedLang !== language) {
      setLanguageState(detectedLang);
    }
  }, [location.pathname]);

  // Custom setLanguage that also updates the URL
  const setLanguage = (newLang) => {
    if (!AVAILABLE_LANGUAGES.includes(newLang)) {
      console.warn(`Language ${newLang} not available`);
      return;
    }

    setLanguageState(newLang);
    
    // Update URL to include language prefix
    const pathParts = location.pathname.split('/').filter(Boolean);
    const currentLang = AVAILABLE_LANGUAGES.includes(pathParts[0]) ? pathParts[0] : null;
    
    let newPath;
    if (currentLang) {
      // Replace existing language prefix
      pathParts[0] = newLang;
      newPath = '/' + pathParts.join('/');
    } else {
      // Add language prefix
      newPath = '/' + newLang + location.pathname;
    }
    
    navigate(newPath, { replace: true });
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
